import { SEARCH_PAGE_METADATA } from "./search-metadata.mock";
import {
  SearchCriteria,
  SearchEntityKey,
  SearchPageMetadata,
  SearchResultRow,
  SearchResultsResponse,
} from "./search-metadata.types";
import { searchPatients } from "../patient/model/search-patients";
import { searchEmployees } from "../employee/model/employees";
import { searchVendors } from "../vendor/model/vendors";
import { searchPurchaseOrders } from "../purchase-order/model/purchase-orders";

const SEARCH_API_BASE = "/api/v1/search";

/**
 * Flip to the real backend by setting REACT_APP_USE_MOCK_SEARCH_API=false
 * (or delete the mock branches once the endpoints exist).
 */
const USE_MOCK_SEARCH_API =
  process.env.REACT_APP_USE_MOCK_SEARCH_API !== "false";

const MOCK_LATENCY_MS = 400;

function withMockLatency<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(value), MOCK_LATENCY_MS);
  });
}

async function requestJson<T>(
  url: string,
  init?: globalThis.RequestInit,
): Promise<T> {
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!response.ok) {
    throw new Error(`Search API request failed: ${response.status} ${url}`);
  }
  return response.json() as Promise<T>;
}

/** GET /api/v1/search/metadata */
export const fetchSearchMetadata = (): Promise<SearchPageMetadata> => {
  if (USE_MOCK_SEARCH_API) {
    return withMockLatency(SEARCH_PAGE_METADATA);
  }
  return requestJson<SearchPageMetadata>(`${SEARCH_API_BASE}/metadata`);
};

type MockSearcher = (
  // eslint-disable-next-line no-unused-vars
  criteria: SearchCriteria,
) => Promise<SearchResultRow[]> | SearchResultRow[];

// employee/vendor/purchaseOrder searchers simulate latency themselves,
// so only the synchronous patient searcher is wrapped here.
const MOCK_SEARCHERS: Record<SearchEntityKey, MockSearcher> = {
  patient: (criteria) => withMockLatency(searchPatients(criteria)),
  employee: (criteria) =>
    searchEmployees(criteria) as unknown as Promise<SearchResultRow[]>,
  vendor: (criteria) =>
    searchVendors(criteria) as unknown as Promise<SearchResultRow[]>,
  purchaseOrder: (criteria) =>
    searchPurchaseOrders(criteria) as unknown as Promise<SearchResultRow[]>,
};

/** POST /api/v1/search/:entity with the criteria as JSON body. */
export const executeSearch = async (
  entity: SearchEntityKey,
  criteria: SearchCriteria,
): Promise<SearchResultsResponse> => {
  if (USE_MOCK_SEARCH_API) {
    const searcher = MOCK_SEARCHERS[entity];
    if (!searcher) {
      throw new Error(`No mock searcher registered for entity "${entity}"`);
    }
    const rows = await searcher(criteria);
    return { entity, total: rows.length, rows };
  }
  return requestJson<SearchResultsResponse>(`${SEARCH_API_BASE}/${entity}`, {
    method: "POST",
    body: JSON.stringify(criteria),
  });
};
